import { useCallback, useRef, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import { useMutation } from '@tanstack/react-query'
import { ApiError, type ReportTemplate } from '@cmms/api-client'
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror'
import { html as htmlLang } from '@codemirror/lang-html'
import { EditorView } from '@codemirror/view'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import {
  renderTemplatePreview, instrumentSourcePositions, SOURCE_POS_ATTR, REPORT_VARIABLE_GROUPS,
} from '../../../lib/reportTemplatePreview'
import { REPORT_TEMPLATE_AI_SPEC } from '../../../lib/reportTemplateAiSpec'

// A4 at 96dpi — matches the actual PDF page width (api/src/lib/htmlToPdf.js
// renders at `format: 'A4'`). The preview iframe is rendered at exactly
// this width, then visually scaled to fit whatever the pane happens to
// be, rather than stretched/squeezed to the pane's own width — a
// template built for a fixed page width (a card centered with margin on
// each side, say) would otherwise overflow a narrower pane edge-to-edge,
// pushing its margins off-screen and making the preview look nothing
// like the real export even though the underlying HTML is identical.
const PAGE_WIDTH_PX = 794

const STARTER_TEMPLATE = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/></head>
<body style="margin:0;padding:24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif">
  <h1 style="font-size:20px;margin-bottom:4px">{{wo.title}}</h1>
  <p style="color:#64748b;font-size:13px">{{wo.code}} · {{site.name}} · {{wo.status}}</p>

  <h3 style="font-size:14px;margin-top:24px">Details</h3>
  <p style="font-size:13px">Priority: {{wo.priority}}<br/>
  Assigned to: {{assigned.name}}<br/>
  Reporter: {{reporter.name}}</p>

  {{checklist_table}}

  {{parts_table}}

  {{signatures_block}}
</body></html>`

// Starter row for {{#checklist_item}}...{{/checklist_item}} — the
// admin edits this freely (columns, styling, which item.* fields to
// show); the server repeats whatever's between the two markers once
// per real checklist answer. Available fields: item.label, item.value,
// item.note ("Note: ..." pre-labeled, or blank — like item.flag_label,
// there's no per-item way to hide a hand-written label only when a
// value exists, so the label travels with the value), item.flag_label
// ("Flagged" or blank), item.section, item.media (pre-rendered
// photo/audio HTML for that answer).
const CHECKLIST_ITEM_LOOP_SNIPPET = `<table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden">
  <thead><tr style="background:#f8fafc">
    <th style="padding:6px 10px;font-size:11px;text-align:left">Item</th>
    <th style="padding:6px 10px;font-size:11px;text-align:left">Response</th>
  </tr></thead>
  <tbody>
    {{#checklist_item}}
    <tr style="page-break-inside:avoid;break-inside:avoid">
      <td style="padding:6px 10px;font-size:12.5px">{{item.label}} {{item.flag_label}}</td>
      <td style="padding:6px 10px;font-size:12.5px">{{item.value}} {{item.note}} {{item.media}}</td>
    </tr>
    {{/checklist_item}}
  </tbody>
</table>`

// CodeMirror's built-in find/replace panel (opened with Cmd/Ctrl-F)
// renders at its default, quite compact size — bump up the input,
// button and label sizing so it's easier to read/click within this
// large source editor.
const searchPanelTheme = EditorView.theme({
  '.cm-panel.cm-search': { fontSize: '14px', padding: '8px 10px', gap: '6px' },
  '.cm-panel.cm-search input.cm-textfield': { fontSize: '14px', padding: '5px 8px' },
  '.cm-panel.cm-search button.cm-button': { fontSize: '13px', padding: '5px 10px' },
  '.cm-panel.cm-search label': { fontSize: '13px', gap: '4px' },
})

/// Source-code HTML editor + click-to-insert variable panel + live
/// preview, for a completion-report template. No WYSIWYG — a
/// syntax-highlighted HTML/CSS source editor (CodeMirror), matching how
/// the report itself is generated (a raw HTML document, screenshotted
/// to PDF client-side — see apps/web/src/lib/printHtml.ts).
export function ReportTemplateEditor({ template, onClose, onSaved }: {
  template: ReportTemplate | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!template
  const [name, setName] = useState(template?.name ?? '')
  const [html, setHtml] = useState(template?.html_content ?? STARTER_TEMPLATE)
  const cmRef = useRef<ReactCodeMirrorRef>(null)
  const [previewScale, setPreviewScale] = useState(1)
  const [previewContentHeight, setPreviewContentHeight] = useState(600)

  // Click a token in the picker → splice text into the editor at
  // wherever the cursor currently is, then restore focus + caret right
  // after the inserted text. Same mechanic as the chat composer's
  // @mention insert (MentionComposer.tsx's pickResult) — just triggered
  // by a click instead of an autocomplete pick, and going through
  // CodeMirror's transaction API (view.dispatch) instead of a plain
  // textarea's selectionStart/setSelectionRange, since the HTML source
  // panel is now a real code editor, not a <textarea>.
  function insertSnippet(insert: string) {
    const view = cmRef.current?.view
    if (!view) { setHtml((prev) => prev + insert); return }
    const { from, to } = view.state.selection.main
    view.dispatch({
      changes: { from, to, insert },
      selection: { anchor: from + insert.length },
    })
    view.focus()
  }
  const insertToken = (token: string) => insertSnippet(`{{${token}}}`)

  // Selects & scrolls to a source range in the code editor — the target
  // of both the variable-picker inserts (via cursor position) and the
  // preview's click-to-inspect (via a clicked element's data-hz-src).
  function selectSourceRange(start: number, end: number) {
    const view = cmRef.current?.view
    if (!view) return
    view.dispatch({ selection: { anchor: start, head: end }, scrollIntoView: true })
    view.focus()
  }

  // Click-to-inspect: hovering an element in the preview iframe outlines
  // it (like Chrome DevTools' element picker); clicking one selects and
  // scrolls to its opening tag in the source editor. The iframe is
  // `srcDoc`-rendered (same origin as the parent), so its document is
  // reachable directly — no injected <script> needed inside the report
  // HTML itself. Re-attached on every 'load' since srcDoc changes (and
  // therefore reloads the iframe) on every keystroke.
  //
  // This has to be a callback ref, not useRef + useEffect(fn, []): the
  // editor lives inside a MUI Dialog, which mounts its portal content
  // one render/effect cycle after the Dialog itself first commits. An
  // empty-deps useEffect on the parent fires before that portal content
  // (the iframe) exists, so `.current` would still read null — the
  // listeners would silently never attach. A callback ref fires exactly
  // when the DOM node itself is attached, regardless of when that is
  // relative to the owning component's own mount.
  // Measures the pane the scaled preview sits in, so previewScale always
  // reflects however wide the pane currently is (dialog resize, window
  // resize) — a callback ref for the same reason attachPreviewFrame
  // below is one: this lives inside a MUI Dialog, whose portal content
  // mounts a cycle after the Dialog itself, so a plain useRef +
  // useEffect(fn, []) would measure before the node exists.
  const attachPreviewPane = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setPreviewScale(Math.min(1, width / PAGE_WIDTH_PX))
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const attachPreviewFrame = useCallback((iframe: HTMLIFrameElement | null) => {
    if (!iframe) return
    const handleLoad = () => {
      const doc = iframe.contentDocument
      if (!doc?.body) return
      const contentHeight = doc.documentElement.scrollHeight
      if (contentHeight > 0) setPreviewContentHeight(contentHeight)
      let hovered: HTMLElement | null = null
      const clearHover = () => {
        if (hovered) { hovered.style.outline = ''; hovered.style.outlineOffset = ''; hovered.style.cursor = ''; hovered = null }
      }
      const findTarget = (e: Event) =>
        (e.target as HTMLElement | null)?.closest?.(`[${SOURCE_POS_ATTR}]`) as HTMLElement | null
      const onMouseOver = (e: MouseEvent) => {
        const el = findTarget(e)
        if (el === hovered) return
        clearHover()
        if (el) {
          el.style.outline = '2px solid #0D9488'
          el.style.outlineOffset = '-2px'
          el.style.cursor = 'pointer'
          hovered = el
        }
      }
      const onClick = (e: MouseEvent) => {
        e.preventDefault()
        const el = findTarget(e)
        const raw = el?.getAttribute(SOURCE_POS_ATTR) ?? ''
        const parts = raw.split(':').map(Number)
        const start = parts[0], end = parts[1]
        if (start !== undefined && end !== undefined && Number.isFinite(start) && Number.isFinite(end)) {
          selectSourceRange(start, end)
        }
      }
      doc.body.addEventListener('mouseover', onMouseOver)
      doc.body.addEventListener('mouseout', clearHover)
      doc.body.addEventListener('click', onClick, true)
    }
    iframe.addEventListener('load', handleLoad)
    return () => iframe.removeEventListener('load', handleLoad)
  }, [])

  const createM = useMutation({
    mutationFn: () => api.reportTemplates.create({ name: name.trim(), html_content: html }),
    onSuccess: () => { toast.success('Template created'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const updateM = useMutation({
    mutationFn: () => api.reportTemplates.update(template!.id, { name: name.trim(), html_content: html }),
    onSuccess: () => { toast.success('Template saved'); onSaved() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const saving = createM.isPending || updateM.isPending
  const dirty = name.trim().length > 0 && html.trim().length > 0

  const copyAiSpec = () => {
    navigator.clipboard.writeText(REPORT_TEMPLATE_AI_SPEC).then(
      () => toast.success('AI spec copied — paste it into ChatGPT or similar, describe the look you want, then paste the HTML back below'),
      () => toast.error('Failed to copy — select and copy manually'),
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, pb: 1.5 }}>
        {isEdit ? 'Edit report template' : 'New report template'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, pb: 0 }}>
        <TextField label="Template name" required autoFocus size="small"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Branded completion report"
          sx={{ mb: 2, maxWidth: 420 }}
        />

        <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          {/* Variable picker */}
          <Box sx={{
            width: 220, flexShrink: 0, overflowY: 'auto', border: '1px solid',
            borderColor: 'divider', borderRadius: 2, p: 1.5,
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
              Insert a variable
            </Typography>
            <Tooltip title="Copies a prompt describing every available variable — paste it into ChatGPT or similar to design a template, then paste the HTML it gives you back into the source panel">
              <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />}
                onClick={copyAiSpec}
                sx={{ mb: 1.5, fontSize: 11.5, fontWeight: 600, borderRadius: 1.5, py: 0.5 }}>
                Copy AI spec
              </Button>
            </Tooltip>
            {REPORT_VARIABLE_GROUPS.map((group) => (
              <Box key={group.label} sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', mb: 0.5 }}>
                  {group.label}
                </Typography>
                <Stack spacing={0.25}>
                  {group.tokens.map((token) => (
                    <Box key={token} component="button" type="button" onClick={() => insertToken(token)}
                      sx={{
                        textAlign: 'left', fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                        px: 0.75, py: 0.4, borderRadius: 1, border: 'none',
                        bgcolor: 'transparent', color: 'primary.main', cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}>
                      {`{{${token}}}`}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}

            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.disabled', mb: 0.5 }}>
                Checklist rows (your own design)
              </Typography>
              <Tooltip title="Inserts a starter row you can restyle freely — repeated once per real checklist answer, instead of the fixed {{checklist_table}} layout">
                <Box component="button" type="button" onClick={() => insertSnippet(CHECKLIST_ITEM_LOOP_SNIPPET)}
                  sx={{
                    textAlign: 'left', fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                    px: 0.75, py: 0.4, borderRadius: 1, border: 'none', width: '100%',
                    bgcolor: 'transparent', color: 'primary.main', cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}>
                  {'{{#checklist_item}}…{{/checklist_item}}'}
                </Box>
              </Tooltip>
            </Box>
          </Box>

          {/* HTML source */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 0.75 }}>
              HTML source
            </Typography>
            <Box sx={{
              flex: 1, minHeight: 0, border: '1px solid', borderColor: 'divider',
              borderRadius: 2, overflow: 'hidden',
            }}>
              <CodeMirror
                ref={cmRef}
                value={html}
                onChange={setHtml}
                height="100%"
                style={{ height: '100%', fontSize: 12.5 }}
                extensions={[htmlLang(), searchPanelTheme]}
                basicSetup={{
                  lineNumbers: true, foldGutter: true, bracketMatching: true,
                  closeBrackets: true, highlightActiveLine: true, autocompletion: false,
                }}
              />
            </Box>
          </Box>

          {/* Live preview */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'text.secondary', mb: 0.75 }}>
              Preview — sample data, click an element to jump to its source
            </Typography>
            <Box ref={attachPreviewPane} sx={{
              flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
              overflow: 'auto', bgcolor: '#eceef1',
            }}>
              <Box style={{ width: PAGE_WIDTH_PX * previewScale, height: previewContentHeight * previewScale }}>
                <Box style={{
                  width: PAGE_WIDTH_PX, height: previewContentHeight,
                  transform: `scale(${previewScale})`, transformOrigin: 'top left',
                }}>
                  <iframe ref={attachPreviewFrame} title="Report template preview"
                    srcDoc={renderTemplatePreview(instrumentSourcePositions(html))}
                    style={{ width: PAGE_WIDTH_PX, height: previewContentHeight, border: 'none', display: 'block', background: '#fff' }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!dirty || saving}
          onClick={() => (isEdit ? updateM.mutate() : createM.mutate())}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
